import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Stack,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
  IconButton,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

/** 3-letter day keys to keep layout compact. */
export type DayKey = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";
export const DAY_ORDER: DayKey[] = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
];
export type TimeHHMM = string; // "HH:MM" 24-hour format

export interface DayInterval {
  start: TimeHHMM; // inclusive
  end: TimeHHMM; // exclusive logically; UI doesn’t enforce overlap rules here
}
export interface DaySchedule {
  active: boolean;
  intervals: DayInterval[];
}
export interface ScheduleRule {
  timezone?: string | null;
  byDay: Record<DayKey, DaySchedule>;
}

export const DEFAULT_SCHEDULE_RULE: ScheduleRule = {
  timezone: null,
  byDay: Object.fromEntries(
    DAY_ORDER.map((d) => [d, { active: false, intervals: [] } as DaySchedule])
  ) as Record<DayKey, DaySchedule>,
};

/** 30-min increments from 00:00..23:30 */
const INCREMENTS_30M: TimeHHMM[] = (() => {
  const out: string[] = [];
  for (let h = 0; h < 24; h++)
    for (const m of [0, 30])
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
  return out;
})();

/** Format to “9:00 AM” */
function fmt12h(t: TimeHHMM): string {
  const [hh, mm] = t.split(":").map(Number);
  const am = hh < 12;
  const h12 = ((hh + 11) % 12) + 1;
  return `${h12}:${String(mm).padStart(2, "0")} ${am ? "AM" : "PM"}`;
}
function dayLabel(d: DayKey) {
  return d;
}

/** Compact human summary like “Mon–Fri 9:00 AM–5:00 PM; Sat–Sun 10:00 AM–2:00 PM (PST)” */
export function buildScheduleSummary(rule: ScheduleRule): string {
  const sig = (intervals: DayInterval[]) =>
    intervals.map((iv) => `${iv.start}-${iv.end}`).join(",");
  const chunks: { from: DayKey; to: DayKey; intervals: DayInterval[] }[] = [];

  let i = 0;
  while (i < DAY_ORDER.length) {
    const d = DAY_ORDER[i];
    const ds = rule.byDay[d];
    if (!ds?.active || !ds.intervals.length) {
      i++;
      continue;
    }
    const s0 = sig(ds.intervals);
    let j = i + 1;
    while (
      j < DAY_ORDER.length &&
      rule.byDay[DAY_ORDER[j]]?.active &&
      sig(rule.byDay[DAY_ORDER[j]].intervals) === s0
    )
      j++;
    chunks.push({
      from: DAY_ORDER[i],
      to: DAY_ORDER[j - 1],
      intervals: ds.intervals,
    });
    i = j;
  }

  if (!chunks.length) return "No schedule specified";

  const rangeLabel = (a: DayKey, b: DayKey) =>
    a === b ? dayLabel(a) : `${dayLabel(a)}–${dayLabel(b)}`;
  const parts = chunks.map((c) => {
    const ints = c.intervals
      .map((iv) => `${fmt12h(iv.start)}–${fmt12h(iv.end)}`)
      .join(", ");
    return `${rangeLabel(c.from, c.to)} ${ints}`;
  });

  return parts.join("; ") + (rule.timezone ? ` (${rule.timezone})` : "");
}

/** Immutable clone helper */
function cloneRule(rule: ScheduleRule): ScheduleRule {
  return {
    timezone: rule.timezone ?? null,
    byDay: Object.fromEntries(
      DAY_ORDER.map((d) => [
        d,
        {
          active: rule.byDay[d]?.active ?? false,
          intervals: (rule.byDay[d]?.intervals ?? []).map((iv) => ({ ...iv })),
        },
      ])
    ) as Record<DayKey, DaySchedule>,
  };
}

function isValidInterval(iv: DayInterval): boolean {
  return iv.start !== iv.end; // simple check; add overlap checks if desired
}

type Props = {
  value?: ScheduleRule;
  onChange?: (next: ScheduleRule) => void;
  onGenerateSummary?: (summary: string) => void;
  disabled?: boolean;
  actionLabel?: string; // default: "Copy & paste into Description"
};

/**
 * Collapsible weekly scheduler with:
 *  - Top-level Accordion for the whole section
 *  - Per-day Accordions to collapse intervals
 *  - “All days (Mon–Sun)” master toggle
 *  - “All day (24h)” for a single day or for all days when master is on
 *  - Presets + summary with single copy/paste button
 */
export default function ScheduleSection({
  value = DEFAULT_SCHEDULE_RULE,
  onChange,
  onGenerateSummary,
  disabled,
  actionLabel = "Copy & paste into Description",
}: Props) {
  const rule = value ?? DEFAULT_SCHEDULE_RULE;

  /** ---------- bulk helpers ---------- */
  const allDaysActive = DAY_ORDER.every((d) => rule.byDay[d]?.active);
  const setAllDaysActive = (active: boolean) => {
    const next = cloneRule(rule);
    DAY_ORDER.forEach((d) => {
      next.byDay[d].active = active;
      if (active && next.byDay[d].intervals.length === 0) {
        next.byDay[d].intervals = [{ start: "09:00", end: "17:00" }];
      }
      if (!active) {
        next.byDay[d].intervals = [];
      }
    });
    onChange?.(next);
  };

  const setAllDaysAllDay = (allDay: boolean) => {
    const next = cloneRule(rule);
    DAY_ORDER.forEach((d) => {
      if (!next.byDay[d].active) return;
      next.byDay[d].intervals = allDay
        ? [{ start: "00:00", end: "23:59" }]
        : [{ start: "09:00", end: "17:00" }];
    });
    onChange?.(next);
  };

  /** ---------- per-day helpers ---------- */
  const toggleDay = (d: DayKey, active: boolean) => {
    const next = cloneRule(rule);
    next.byDay[d].active = active;
    if (active && next.byDay[d].intervals.length === 0) {
      next.byDay[d].intervals = [{ start: "09:00", end: "17:00" }];
    }
    if (!active) next.byDay[d].intervals = [];
    onChange?.(next);
  };

  const addInterval = (d: DayKey) => {
    const next = cloneRule(rule);
    next.byDay[d].active = true;
    next.byDay[d].intervals.push({ start: "09:00", end: "17:00" });
    onChange?.(next);
  };

  const removeInterval = (d: DayKey, idx: number) => {
    const next = cloneRule(rule);
    next.byDay[d].intervals.splice(idx, 1);
    onChange?.(next);
  };

  const setIntervalField = (
    d: DayKey,
    idx: number,
    field: "start" | "end",
    t: TimeHHMM
  ) => {
    const next = cloneRule(rule);
    (next.byDay[d].intervals[idx] as any)[field] = t;
    onChange?.(next);
  };

  const setDayAllDay = (d: DayKey, allDay: boolean) => {
    const next = cloneRule(rule);
    next.byDay[d].active = true;
    next.byDay[d].intervals = allDay
      ? [{ start: "00:00", end: "23:59" }]
      : next.byDay[d].intervals.length
      ? next.byDay[d].intervals
      : [{ start: "09:00", end: "17:00" }];
    onChange?.(next);
  };

  // Presets
  const applyWeekdays95 = () => {
    const next = cloneRule(rule);
    DAY_ORDER.forEach((d) => {
      next.byDay[d].active = d !== "Sat" && d !== "Sun";
      next.byDay[d].intervals = next.byDay[d].active
        ? [{ start: "09:00", end: "17:00" }]
        : [];
    });
    onChange?.(next);
  };
  const applyNights = () => {
    const next = cloneRule(rule);
    DAY_ORDER.forEach((d) => {
      next.byDay[d].active = true;
      next.byDay[d].intervals = [
        { start: "22:00", end: "23:59" },
        { start: "00:00", end: "06:00" },
      ];
    });
    onChange?.(next);
  };
  const applyWeekends95 = () => {
    const next = cloneRule(rule);
    DAY_ORDER.forEach((d) => {
      const wknd = d === "Sat" || d === "Sun";
      next.byDay[d].active = wknd;
      next.byDay[d].intervals = wknd ? [{ start: "09:00", end: "17:00" }] : [];
    });
    onChange?.(next);
  };

  const summary = buildScheduleSummary(rule);

  const copyAndNotify = async () => {
    try {
      await navigator.clipboard.writeText(summary);
    } catch {}
    try {
      window.dispatchEvent(
        new CustomEvent("plany:add-schedule-summary", { detail: { summary } })
      );
    } catch {}
    onGenerateSummary?.(summary);
  };

  return (
    <Accordion
      disableGutters
      sx={{ borderRadius: 2, border: "1px solid", borderColor: "divider" }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">Schedule</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Stack spacing={2}>
          {/* Presets + master toggles */}
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", alignItems: "center" }}
          >
            <Chip label="Weekdays 9–5" onClick={applyWeekdays95} />
            <Chip label="Nights 10p–6a" onClick={applyNights} />
            <Chip label="Weekends 9–5" onClick={applyWeekends95} />
            <Divider flexItem orientation="vertical" sx={{ mx: 1 }} />
            <FormControlLabel
              control={
                <Checkbox
                  checked={allDaysActive}
                  onChange={(e) => setAllDaysActive(e.target.checked)}
                  disabled={disabled}
                />
              }
              label="All days (Mon–Sun)"
            />
            {allDaysActive && (
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setAllDaysAllDay(e.target.checked)}
                    disabled={disabled}
                  />
                }
                label="All day (24h) for all days"
              />
            )}
          </Stack>

          <Divider />

          {/* Days (each in its own Accordion) */}
          <FormGroup
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 1.25,
            }}
          >
            {DAY_ORDER.map((d) => {
              const day = rule.byDay[d];
              const isAllDay =
                day.active &&
                day.intervals.length === 1 &&
                day.intervals[0].start === "00:00" &&
                day.intervals[0].end === "23:59";

              return (
                <Accordion
                  key={d}
                  disableGutters
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    "&:before": { display: "none" },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ width: "100%", pr: 1 }}
                    >
                      <FormControlLabel
                        onClick={(e) => e.stopPropagation()}
                        onFocus={(e) => e.stopPropagation()}
                        control={
                          <Checkbox
                            checked={day.active}
                            onChange={(e) => toggleDay(d, e.target.checked)}
                            disabled={disabled}
                          />
                        }
                        label={
                          <Typography variant="subtitle1">
                            {dayLabel(d)}
                          </Typography>
                        }
                      />
                      {day.active && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ ml: "auto" }}
                        >
                          {isAllDay
                            ? "All day"
                            : day.intervals.length
                            ? day.intervals
                                .map(
                                  (iv) =>
                                    `${fmt12h(iv.start)}–${fmt12h(iv.end)}`
                                )
                                .join(", ")
                            : "No intervals"}
                        </Typography>
                      )}
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    {day.active ? (
                      <Stack spacing={1.25}>
                        {/* per-day All day */}
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={isAllDay}
                              onChange={(e) =>
                                setDayAllDay(d, e.target.checked)
                              }
                              disabled={disabled}
                            />
                          }
                          label="All day (24h)"
                        />

                        {!isAllDay && (
                          <>
                            {day.intervals.map((iv, idx) => {
                              const valid = isValidInterval(iv);
                              return (
                                <Stack
                                  key={idx}
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                  sx={{ flexWrap: "wrap" }}
                                >
                                  <TimeSelect
                                    label="Start"
                                    value={iv.start}
                                    onChange={(t) =>
                                      setIntervalField(d, idx, "start", t)
                                    }
                                    disabled={disabled}
                                  />
                                  <Typography variant="body2">to</Typography>
                                  <TimeSelect
                                    label="End"
                                    value={iv.end}
                                    onChange={(t) =>
                                      setIntervalField(d, idx, "end", t)
                                    }
                                    disabled={disabled}
                                  />
                                  <IconButton
                                    aria-label="Remove interval"
                                    onClick={() => removeInterval(d, idx)}
                                    disabled={disabled}
                                    size="small"
                                  >
                                    <DeleteOutlineIcon fontSize="small" />
                                  </IconButton>
                                  {!valid && (
                                    <Typography variant="caption" color="error">
                                      Invalid range (start and end cannot be
                                      equal).
                                    </Typography>
                                  )}
                                </Stack>
                              );
                            })}
                            <Button
                              size="small"
                              startIcon={<AddCircleOutlineIcon />}
                              variant="outlined"
                              onClick={() => addInterval(d)}
                              disabled={disabled}
                            >
                              Add interval
                            </Button>
                          </>
                        )}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Enable {dayLabel(d)} to add intervals.
                      </Typography>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </FormGroup>

          {/* Summary + one action */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Summary (copy & paste into Description)
            </Typography>
            <Alert
              severity="success"
              icon={false}
              sx={{
                py: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
              }}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  onClick={copyAndNotify}
                  disabled={disabled}
                >
                  {actionLabel ?? "Copy & paste into Description"}
                </Button>
              }
            >
              {summary}
            </Alert>
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}

/** Simple 30-min time picker using Select */
function TimeSelect({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: TimeHHMM;
  onChange: (t: TimeHHMM) => void;
  disabled?: boolean;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 140 }} disabled={disabled}>
      <InputLabel id={`${label}-label`}>{label}</InputLabel>
      <Select
        labelId={`${label}-label`}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as TimeHHMM)}
        renderValue={(v) => fmt12h(v as TimeHHMM)}
      >
        {INCREMENTS_30M.map((t) => (
          <MenuItem key={t} value={t}>
            {fmt12h(t)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
